// src/components/LayoutWrapper.tsx
import React, { useState, useEffect } from "react";
import { useLocation, Outlet, useNavigate } from "react-router-dom";
import axios from "axios";

import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import { TenantSubscriptionAlert } from "../components/TenantBranding";
import { getApiUrl } from "../config/api";

const LayoutWrapper: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth > 1024;
  });
  const [role, setRole] = useState<
    "superadmin" | "admin" | "headteacher" | "teacher" | "student" | undefined
  >(undefined);

  // Hide navbar and sidebar on auth pages
  const normalizedPath = location.pathname.toLowerCase();
  const subscriptionBlocked = localStorage.getItem("subscriptionBlocked") === "true";
  const hideNavbarAndSidebar =
    normalizedPath === "/" ||
    normalizedPath === "/signin" ||
    normalizedPath === "/signup" ||
    normalizedPath === "/forgot-password" ||
    normalizedPath.startsWith("/reset-password") ||
    (subscriptionBlocked && normalizedPath !== "/settings/subscription");

  useEffect(() => {
    const stored = (localStorage.getItem("role") || "").toLowerCase();
    if (
      stored === "superadmin" ||
      stored === "admin" ||
      stored === "headteacher" ||
      stored === "teacher" ||
      stored === "student"
    ) {
      setRole(stored as any);
    } else {
      setRole(undefined);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth <= 1024) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.hash) return;

    const resetScrollPosition = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      const mainWrapper = document.querySelector(".main-wrapper");
      if (mainWrapper instanceof HTMLElement) {
        mainWrapper.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }

      const dashboardMain = document.querySelector(".dashboard-main");
      if (dashboardMain instanceof HTMLElement) {
        dashboardMain.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    };

    const frameId = window.requestAnimationFrame(resetScrollPosition);
    return () => window.cancelAnimationFrame(frameId);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const checkMaintenance = async () => {
      if (hideNavbarAndSidebar) return;
      if (!role || role === "superadmin") return;

      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const res = await axios.get(getApiUrl("maintenance/status"), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data?.active) {
          navigate("/maintenance");
        }
      } catch {
        // Ignore maintenance check errors
      }
    };

    checkMaintenance();
  }, [hideNavbarAndSidebar, role, navigate]);

  return (
    <>
      {!hideNavbarAndSidebar && (
        <>
          <Sidebar
            isOpen={isSidebarOpen}
            setIsOpen={setIsSidebarOpen}
            role={role}
          />
          {/* Navbar handles its own positioning via fixed class */}
          <Navbar className={isSidebarOpen ? "shifted" : "collapsed"} role={role} />
        </>
      )}

      <main
        className={`main-wrapper ${!hideNavbarAndSidebar
          ? isSidebarOpen
            ? "shifted"
            : "collapsed"
          : "full-top"
          }`}
      >
        {/* Don't show subscription alerts for superadmin - they manage tenants, not subject to tenant suspension */}
        {!hideNavbarAndSidebar && role !== 'superadmin' && <TenantSubscriptionAlert className="mb-4" />}
        <Outlet />
      </main>
    </>
  );
};

export default LayoutWrapper;
